import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { forgotPassword } from '@/api/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { EmailStepSchema, type EmailStepValues } from './ForgotPasswordSchema';

type Step = 'email' | 'sent';

export default function ForgotPassword() {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  if (step === 'sent') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Check your email</CardTitle>
          <CardDescription>
            If an account exists for {email}, we&apos;ve sent a password reset link.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertDescription>
              Open the link in that email to choose a new password. The link is valid
              for 60 minutes. Remember to check your spam folder.
            </AlertDescription>
          </Alert>
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => {
              setStep('email');
              setError(null);
            }}
          >
            Use a different email
          </Button>
          <div className="text-center text-sm">
            <Link to="/auth/login" className="text-primary hover:underline">
              Back to sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Reset your password</CardTitle>
        <CardDescription>
          Enter your email and we&apos;ll send you a link to set a new password.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <EmailStep
          isLoading={isLoading}
          onSubmit={async (values) => {
            setError(null);
            setIsLoading(true);
            try {
              await forgotPassword(values.email);
              setEmail(values.email);
              setStep('sent');
            } catch (err) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const e = err as any;
              const detail = e?.response?.data?.detail ?? e?.message ?? '';
              const status = e?.response?.status;
              if (status === 429) {
                setError('Too many attempts. Please wait a few minutes and try again.');
              } else {
                setError(detail || 'Failed to send reset link. Please try again.');
              }
            } finally {
              setIsLoading(false);
            }
          }}
        />

        <div className="text-center text-sm">
          <Link to="/auth/login" className="text-primary hover:underline">
            Back to sign in
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

interface EmailStepProps {
  isLoading: boolean;
  onSubmit: (values: EmailStepValues) => Promise<void> | void;
}

function EmailStep({ isLoading, onSubmit }: EmailStepProps) {
  const form = useForm<EmailStepValues>({
    resolver: zodResolver(EmailStepSchema),
    defaultValues: { email: '' },
    mode: 'onTouched',
  });

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email address</FormLabel>
              <FormControl>
                <Input {...field} type="email" autoComplete="email" autoFocus disabled={isLoading} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? 'Sending…' : 'Send reset link'}
        </Button>
      </form>
    </Form>
  );
}
